import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { Upload, FileText, Image as ImageIcon, Trash2, Download, Eye, Camera, FileSignature, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/shared";

const CHUNK = 1024 * 1024;

const shrinkImage = (file) => new Promise((resolve) => {
  if (!file.type.startsWith("image/") || file.type === "image/heic" || file.type === "image/heif" || file.size < 1.2 * 1024 * 1024) return resolve(file);
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const max = 2200; const scale = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas"); c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => { URL.revokeObjectURL(url); resolve(b ? new File([b], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }) : file); }, "image/jpeg", 0.86);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

export const uploadDocument = async (patientId, file, meta, onProgress) => {
  const f = await shrinkImage(file);
  const init = await axios.post(`${API}/patients/${patientId}/documents/upload/init`, { filename: f.name, content_type: f.type, size: f.size, ...meta });
  const { upload_id } = init.data;
  const total = Math.ceil(f.size / CHUNK);
  for (let i = 0; i < total; i++) {
    await axios.put(`${API}/patients/${patientId}/documents/upload/${upload_id}/chunk/${i}`, f.slice(i * CHUNK, (i + 1) * CHUNK), { headers: { "Content-Type": "application/octet-stream" } });
    onProgress?.(Math.round(((i + 1) / total) * 90));
  }
  const done = await axios.post(`${API}/patients/${patientId}/documents/upload/${upload_id}/complete`);
  onProgress?.(100);
  return done.data;
};

const Thumb = ({ patientId, doc }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!doc.content_type.startsWith("image/")) return;
    let url; axios.get(`${API}/patients/${patientId}/documents/${doc.id}/file`, { responseType: "blob" }).then(r => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [patientId, doc.id, doc.content_type]);
  if (!doc.content_type.startsWith("image/")) return <div className="w-16 h-16 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0"><FileText className="w-7 h-7 text-primary" /></div>;
  return src ? <img src={src} alt={doc.title} className="w-16 h-16 rounded-lg object-cover border border-border/50 shrink-0" /> : <div className="w-16 h-16 rounded-lg bg-muted/30 border border-border/40 flex items-center justify-center shrink-0"><ImageIcon className="w-6 h-6 text-muted-foreground" /></div>;
};

export const DocumentsTab = ({ patient }) => {
  const [docs, setDocs] = useState([]);
  const [consents, setConsents] = useState([]);
  const [cats, setCats] = useState([]);
  const [pending, setPending] = useState(null);
  const [category, setCategory] = useState("Photo ID");
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const fileRef = useRef(); const camRef = useRef();

  const load = useCallback(() => {
    axios.get(`${API}/patients/${patient.id}/documents`).then(r => setDocs(r.data)).catch(() => {});
    axios.get(`${API}/patients/${patient.id}/consent-forms`).then(r => setConsents(r.data)).catch(() => {});
  }, [patient.id]);
  useEffect(() => { load(); axios.get(`${API}/documents/categories`).then(r => setCats(r.data)).catch(() => {}); }, [load]);

  const pick = (e) => { const f = e.target.files?.[0]; if (f) { setPending(f); setTitle(f.name.replace(/\.\w+$/, "")); } e.target.value = ""; };
  const doUpload = async () => {
    if (!pending) return;
    setProgress(0);
    try { await uploadDocument(patient.id, pending, { category, title }, setProgress); toast.success("Document saved to chart"); setPending(null); setTitle(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setProgress(null); }
  };
  const open = async (doc, download) => {
    try {
      const r = await axios.get(`${API}/patients/${patient.id}/documents/${doc.id}/file`, { params: { download }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: doc.content_type }));
      if (download) { const a = document.createElement("a"); a.href = url; a.download = doc.original_filename; a.click(); } else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open document"); }
  };
  const openConsentPdf = async (f, download) => {
    try {
      const r = await axios.get(`${API}/patients/${patient.id}/consent-forms/${f.id}/pdf`, { params: { download }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (download) { const a = document.createElement("a"); a.href = url; a.download = `EliteIV-Consent-${patient.last_name}-${f.signed_at.slice(0, 10)}.pdf`; a.click(); } else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not generate PDF"); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/patients/${patient.id}/documents/${confirmDel.id}`); toast.success("Document removed"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="chart-documents-tab">
      <div className="rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 p-4" data-testid="document-upload-box">
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid="document-file-input" />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="document-camera-input" />
        {!pending ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm flex items-center gap-2"><Upload className="w-4 h-4 text-primary" />Add to document vault</p>
              <p className="text-xs text-muted-foreground">Photo ID, insurance card, paper intake/consent forms, labs — JPG, PNG, HEIC or PDF up to 15 MB. Stored encrypted; every open is audit-logged.</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => camRef.current?.click()} className="h-8 text-xs border-primary/40 text-primary" data-testid="document-take-photo"><Camera className="w-3.5 h-3.5 mr-1" />Take photo</Button>
              <Button size="sm" onClick={() => fileRef.current?.click()} className="h-8 text-xs bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold" data-testid="document-choose-file"><Upload className="w-3.5 h-3.5 mr-1" />Choose file</Button>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-[1fr_200px_auto] gap-3 items-end" data-testid="document-pending">
            <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} data-testid="document-title" /><p className="text-[10px] text-muted-foreground truncate">{pending.name} · {(pending.size / 1024 / 1024).toFixed(2)} MB</p></div>
            <div className="space-y-1"><Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="document-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map(c => <SelectItem key={c} value={c} data-testid={`document-category-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setPending(null)} disabled={progress !== null} className="h-9">Cancel</Button>
              <Button size="sm" onClick={doUpload} disabled={progress !== null} className="h-9 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold" data-testid="document-upload-submit">{progress !== null ? `Uploading ${progress}%` : "Save to chart"}</Button>
            </div>
            {progress !== null && <div className="sm:col-span-3 h-1.5 rounded-full bg-black/40 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>}
          </div>
        )}
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Uploaded documents ({docs.length})</p>
        {docs.length === 0 && <p className="text-xs text-muted-foreground" data-testid="documents-empty">No documents uploaded yet.</p>}
        <div className="space-y-2">
          {docs.map(d => (
            <div key={d.id} className="rounded-lg border border-border/50 bg-card/40 p-3 flex items-center gap-3" data-testid={`document-row-${d.id}`}>
              <Thumb patientId={patient.id} doc={d} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{d.title}</p>
                <p className="text-xs text-muted-foreground"><span className="text-primary">{d.category}</span> · {(d.size / 1024).toFixed(0)} KB · {format(parseISO(d.created_at), 'MMM d, yyyy h:mm a')} · by {d.uploaded_by_name}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 border-primary/40 text-primary" onClick={() => open(d, false)} data-testid={`document-view-${d.id}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" onClick={() => open(d, true)} data-testid={`document-download-${d.id}`}><Download className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2 text-rose-300 hover:text-rose-200" onClick={() => setConfirmDel(d)} data-testid={`document-delete-${d.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Signed HIPAA & consent forms ({consents.length})</p>
        {consents.length === 0 && <p className="text-xs text-muted-foreground">No signed forms yet — see the Consent tab to send or sign.</p>}
        <div className="space-y-2">
          {consents.map(f => (
            <div key={f.id} className="rounded-lg border border-emerald-500/30 bg-emerald-950/10 p-3 flex items-center gap-3" data-testid={`vault-consent-${f.id}`}>
              <div className="w-16 h-16 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0"><FileSignature className="w-7 h-7 text-emerald-300" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{f.forms.join(' + ')}</p>
                <p className="text-xs text-muted-foreground">Signed by <span className="italic font-serif text-foreground">{f.signature_name}</span> · {format(parseISO(f.signed_at), 'MMM d, yyyy h:mm a')} · {f.method === 'in_person' ? 'in person' : 'electronic'}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 border-primary/40 text-primary" onClick={() => openConsentPdf(f, false)} data-testid={`vault-consent-print-${f.id}`}><Printer className="w-3 h-3 mr-1" />Print</Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" onClick={() => openConsentPdf(f, true)} data-testid={`vault-consent-download-${f.id}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="document-delete-dialog">
          <DialogHeader><DialogTitle>Remove document?</DialogTitle><DialogDescription>"{confirmDel?.title}" will be removed from the chart. This action is recorded in the audit log.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmDel(null)}>Keep</Button><Button onClick={remove} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="document-delete-confirm">Remove</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
